<template>
  <div class="w-full space-y-4 text-slate-100">
    <!-- TOP BAR -->
    <div
      class="min-w-0 border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg"
      style="border-radius: 26px;"
    >
      <div class="flex flex-col gap-4 px-4 py-4 sm:px-5 sm:py-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <!-- LEFT -->
          <div class="min-w-0">
            <nav class="hidden sm:flex" aria-label="Breadcrumb">
              <ol role="list" class="flex items-center gap-2 text-xs text-slate-400">
                <li class="flex items-center">
                  <router-link
                    to="/hub"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-70 text-slate-200 transition hover:bg-slate-600"
                  >
                    <HomeIcon class="h-4 w-4" aria-hidden="true" />
                    <span class="sr-only">Home</span>
                  </router-link>
                </li>

                <li class="flex items-center gap-2">
                  <svg
                    class="h-4 w-4 text-slate-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  <router-link
                    to="/dashboard/routes"
                    class="font-medium text-slate-300 transition hover:text-slate-100"
                  >
                    Trasy
                  </router-link>
                </li>

                <li class="flex items-center gap-2">
                  <svg
                    class="h-4 w-4 text-slate-500"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="2"
                  >
                    <path stroke-linecap="round" stroke-linejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                  <span class="font-medium text-slate-300">Regiony tras</span>
                </li>
              </ol>
            </nav>

            <div class="flex items-center sm:hidden">
              <router-link
                to="/dashboard/routes"
                class="inline-flex items-center gap-2 rounded-xl border border-slate-600 bg-slate-700 px-3 py-2 text-sm font-medium text-slate-200 transition hover:bg-slate-600"
              >
                &larr; Cofnij
              </router-link>
            </div>

            <div class="mt-3">
              <div class="text-lg font-semibold tracking-tight text-white">
                Zarządzanie regionami tras
              </div>
              <div class="mt-1 text-xs text-slate-400">
                Dodawaj, edytuj i porządkuj regiony wykorzystywane do przypisywania tras i obszarów dostaw.
              </div>
            </div>
          </div>

          <!-- RIGHT -->
          <div class="flex shrink-0 items-center">
            <button
              type="button"
              @click.prevent="addRegion"
              class="inline-flex items-center gap-2 rounded-2xl border border-slate-600 bg-slate-700 px-4 py-2.5 text-xs font-semibold text-slate-100 shadow-sm transition hover:bg-slate-600"
            >
              <svg
                class="h-4 w-4"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 5v14M5 12h14" />
              </svg>
              Dodaj region
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- TABLE CARD -->
    <div
      class="border border-slate-700 bg-slate-800 bg-opacity-80 shadow-lg"
      style="border-radius: 26px;"
    >
      <div class="border-b border-slate-700 px-4 py-4 sm:px-5">
        <div class="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div class="min-w-0">
            <div class="text-sm font-semibold text-white">
              Regiony tras
            </div>
            <div class="mt-1 text-xs text-slate-400">
              Łącznie pozycji: {{ sortedRegions.length }}
            </div>
          </div>
        </div>
      </div>

      <div class="px-4 py-4 sm:px-5">
        <div
          v-if="!loading"
          class="overflow-hidden rounded-2xl border border-slate-700"
        >
          <div class="w-full min-w-0 overflow-hidden">
            <table class="w-full divide-y divide-slate-700 regionsTable" style="table-layout: fixed;">
              <thead class="bg-slate-700 bg-opacity-70">
                <tr>
                  <!-- ID -->
                  <th
                    scope="col"
                    class="py-3.5 pl-4 pr-3 text-left text-sm font-semibold whitespace-nowrap text-slate-100 sm:pl-6"
                    style="width: 90px;"
                  >
                    <div class="flex items-center gap-1">
                      ID
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'id' && sort.type === 'asc'"
                        @click.prevent="setSort('id', 'desc')"
                        :class="iconCls('id')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('id', 'asc')"
                        :class="iconCls('id')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Nazwa -->
                  <th
                    class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100"
                    style="width: 260px;"
                  >
                    <div class="flex items-center gap-1">
                      Nazwa regionu
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'name' && sort.type === 'asc'"
                        @click.prevent="setSort('name', 'desc')"
                        :class="iconCls('name')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('name', 'asc')"
                        :class="iconCls('name')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- Opis -->
                  <th
                    class="px-3 py-3.5 text-left text-sm font-semibold text-slate-100"
                  >
                    <div class="flex items-center gap-1">
                      Opis
                      <ChevronDownIcon
                        v-if="sort.pickedColumn === 'description' && sort.type === 'asc'"
                        @click.prevent="setSort('description', 'desc')"
                        :class="iconCls('description')"
                        aria-hidden="true"
                      />
                      <ChevronUpIcon
                        v-else
                        @click.prevent="setSort('description', 'asc')"
                        :class="iconCls('description')"
                        aria-hidden="true"
                      />
                    </div>
                  </th>

                  <!-- AKCJE -->
                  <th
                    scope="col"
                    class="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    style="width: 72px;"
                  >
                    <span class="sr-only">Akcje</span>
                  </th>
                </tr>
              </thead>

              <tbody class="divide-y divide-slate-700 bg-slate-800 bg-opacity-40">
                <tr
                  v-for="r in sortedRegions"
                  :key="r.id"
                  class="transition hover:bg-slate-700 hover:bg-opacity-30"
                >
                  <!-- ID -->
                  <td class="whitespace-nowrap py-4 pl-4 pr-3 text-xs text-slate-100 sm:pl-6">
                    <span
                      v-tooltip="r.id"
                      @click.prevent="copyToClipboard(r.id)"
                      class="cursor-pointer font-semibold text-blue-300 transition hover:text-blue-200"
                    >
                      Kopiuj
                    </span>
                  </td>

                  <!-- Nazwa -->
                  <td class="px-3 py-4 text-xs text-slate-100">
                    <div
                      v-tooltip="r.name"
                      class="truncate font-medium"
                      style="max-width: 260px;"
                    >
                      {{ r.name || '—' }}
                    </div>
                  </td>

                  <!-- Opis -->
                  <td class="px-3 py-4 text-xs text-slate-200">
                    <div
                      v-tooltip="r.description || '—'"
                      class="truncate"
                    >
                      {{ r.description || '—' }}
                    </div>
                  </td>

                  <!-- Akcje -->
                  <td
                    class="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 select-none"
                  >
                    <div class="flex items-center justify-end gap-2">
                      <button
                        v-tooltip="`Edycja`"
                        type="button"
                        @click.prevent="editRegion(r)"
                        class="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-600 bg-slate-700 bg-opacity-80 transition hover:bg-slate-600"
                      >
                        <PencilSquareIcon
                          class="h-4 w-4 text-slate-200"
                          aria-hidden="true"
                        />
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>

            <EmptyState
              v-if="regions.length === 0"
              :title="'Brak zdefiniowanych regionów'"
              :subtitle="'Dodaj pierwszy region, aby przypisywać zamówienia do obszarów dostaw.'"
            />
          </div>
        </div>

        <div v-else class="flex items-center justify-center py-10">
          <div class="spinner"></div>
        </div>
      </div>
    </div>

    <ModalNewRegion
      v-if="showAddModal"
      :show="showAddModal"
      @closeNewRegionModal="showAddModal = false"
      @reloadRegions="loadRegions"
    />

    <ModalManageRouteRegion
      v-if="showEditModal"
      :show="showEditModal"
      :managedRegion="pickedRegion"
      @closeRegionModal="closeEditModal"
      @reloadRegions="loadRegions"
    />
  </div>
</template>

<script>
import { HomeIcon, ChevronDownIcon, ChevronUpIcon } from '@heroicons/vue/24/solid'
import { PencilSquareIcon } from '@heroicons/vue/24/outline'
import EmptyState from '@/components/EmptyState.vue'
import ModalNewRegion from '@/components/ModalNewRegion.vue'
import ModalManageRouteRegion from '@/components/ModalManageRouteRegion.vue'
import { db } from '@/firebase/gfbconf.js'

export default {
  name: 'ManageRouteRegions',
  components: {
    HomeIcon,
    ChevronDownIcon,
    ChevronUpIcon,
    PencilSquareIcon,
    EmptyState,
    ModalNewRegion,
    ModalManageRouteRegion
  },
  data() {
    return {
      loading: false,
      regions: [],
      sort: {
        pickedColumn: 'name',
        type: 'asc'
      },
      showEditModal: false,
      showAddModal: false,
      pickedRegion: null
    }
  },
  created() {
    this.loadRegions()
  },
  computed: {
    sortedRegions() {
      const list = [...this.regions]
      const col = this.sort.pickedColumn
      const dir = this.sort.type === 'asc' ? 1 : -1

      return list.sort((a, b) => {
        const va = a?.[col]
        const vb = b?.[col]

        const sa = (va ?? '').toString().toLowerCase()
        const sb = (vb ?? '').toString().toLowerCase()

        if (sa < sb) return -1 * dir
        if (sa > sb) return 1 * dir
        return 0
      })
    }
  },
  methods: {
    async loadRegions() {
      this.loading = true
      try {
        const doc = await db.collection('routeRegions').doc('regions').get()
        if (doc.exists) {
          const data = doc.data()
          this.regions = Array.isArray(data.regions) ? data.regions : []
          console.log(this.regions)
        } else {
          this.regions = []
        }
      } catch (e) {
        console.error('Błąd pobierania regionów:', e)
        this.regions = []
        this.$store?.commit('setNotification', {
          show: true,
          head: 'Błąd pobierania regionów',
          subheader: 'Nie udało się odczytać dokumentu routeRegions/regions.',
          success: false
        })
      } finally {
        this.loading = false
      }
    },

    setSort(column, type) {
      this.sort.pickedColumn = column
      this.sort.type = type
    },

    iconCls(col) {
      return {
        'h-4 w-4 cursor-pointer transition hover:text-blue-300': true,
        'opacity-30': this.sort.pickedColumn !== col,
        'text-slate-300': true
      }
    },

    truncate(value, max) {
      if (!value) return ''
      const str = value.toString()
      if (str.length <= max) return str
      return str.slice(0, max - 1) + '…'
    },

    addRegion() {
      this.showAddModal = true
    },

    editRegion(region) {
      this.pickedRegion = region
      this.showEditModal = true
    },

    copyToClipboard(text) {
      if (!text) return
      navigator.clipboard.writeText(text).then(() => {
        this.$store?.commit('setNotification', {
          show: true,
          head: 'Skopiowano do schowka!',
          subheader: '',
          success: true
        })
      })
    },

    closeEditModal() {
      this.showEditModal = false
      this.pickedRegion = null
    }
  }
}
</script>

<style lang="scss" scoped>
.spinner {
  width: 1.4rem;
  height: 1.4rem;
  border-radius: 50%;
  border: 5px solid #475569;
  animation: spinner-bulqg1 1.3s infinite linear alternate, spinner-oaa3wk 2.6s infinite linear;
}

@keyframes spinner-bulqg1 {
  0% { clip-path: polygon(50% 50%, 0 0, 50 0, 50 0, 50 0, 50 0, 50 0); }
  12.5% { clip-path: polygon(50% 50%, 0 0, 50 0, 100 0, 100 0, 100 0, 100 0); }
  25% { clip-path: polygon(50% 50%, 0 0, 50 0, 100 0, 100 100, 100 100, 100 100); }
  50% { clip-path: polygon(50% 50%, 0 0, 50 0, 100 0, 100 100, 50 100, 0 100); }
  62.5% { clip-path: polygon(50% 50%, 100 0, 100 0, 100 0, 100 100, 50 100, 0 100); }
  75% { clip-path: polygon(50% 50%, 100 100, 100 100, 100 100, 100 100, 50 100, 0 100); }
  100% { clip-path: polygon(50% 50%, 50 100, 50 100, 50 100, 50 100, 50 100, 0 100); }
}

@keyframes spinner-oaa3wk {
  0% { transform: scaleY(1) rotate(0deg); }
  49.99% { transform: scaleY(1) rotate(135deg); }
  50% { transform: scaleY(-1) rotate(0deg); }
  100% { transform: scaleY(-1) rotate(-135deg); }
}

.regionsTable th,
.regionsTable td {
  overflow: hidden;
}

.regionsTable th > div,
.regionsTable td > div {
  min-width: 0;
}
</style>